<template>
  <div>
    <b-collapse
      v-model="isOpen"
      class="card has-background-color-background is-border-radius-8"
      :class="{ 'has-border-danger': hasError }"
      animation="slide">

      <!-- Header -->
      <div
        slot="trigger"
        slot-scope="props"
        class="card-header no-box-shadow px-10"
        role="button">
        <div class="card-header-icon">
          <b-icon :icon="props.open ? 'chevron-down' : 'chevron-up'"></b-icon>
        </div>
        <p class="card-header-title is-size-14px">
          {{ title }}
        </p>
        <p
          v-if="!isGeneration"
          class="card-header-icon has-text-black has-text-weight-bold is-size-14px">
          {{ func.formatNombre(budget.prixTotal) }}
        </p>
      </div>

      <!-- Content -->
      <div class="card-content px-10 pt-0 pb-10">
        <MsgError
          v-if="msgErrors.length"
          type="is-danger"
          :msgs="msgErrors"
          @close="msgErrorClose"/>
        <div class="columns is-multiline is-marginless">

          <!-- Date de Début -->
          <div class="column" :class="isGeneration ? 'is-4' : 'is-6'">
            <b-field
              custom-class="mb-4px"
              :type="error.dateDebut ? 'is-danger' : ''">
              <template slot="label">
                <qd-label
                  :label="$t('creerPanneau.labelChamp.dateDebut')"
                  :required="true" />
              </template>
              <b-datepicker
                v-model="budget.dateDebut"
                position="is-bottom-right"
                icon="calendar-today"
                editable
                @blur="initialiseFraisTech"
                @input="onDateDebutInput"
                :max-date="budget.dateFin"
                :disabled="disabled">
              </b-datepicker>
            </b-field>
          </div>

          <!-- Date 1er echeance -->
          <div
            v-if="isGeneration"
            class="column is-4">
            <b-field custom-class="mb-4px">
              <template slot="label">
                <qd-label :label="$t('creerPanneau.labelChamp.dateFinPremiereEcheance')" />
              </template>
              <b-datepicker
                v-model="budget.dateFinPremiereEcheance"
                position="is-bottom-left"
                icon="calendar-today"
                editable
                :min-date="budget.dateDebut"
                :max-date="budget.dateFin"
                :disabled="disabled">
              </b-datepicker>
            </b-field>
          </div>

          <!-- Date de Fin -->
          <div class="column" :class="isGeneration ? 'is-4' : 'is-6'">
            <b-field
              custom-class="mb-4px"
              :type="error.dateFin ? 'is-danger' : ''">
              <template slot="label">
                <qd-label
                  :label="$t('creerPanneau.labelChamp.dateFin')"
                  :required="true" />
              </template>
              <b-datepicker
                v-model="budget.dateFin"
                position="is-bottom-left"
                icon="calendar-today"
                editable
                @input="onDateFinInput"
                :min-date="budget.dateDebut"
                :disabled="disabled">
              </b-datepicker>
            </b-field>
          </div>

          <!-- Prix unitaire brut + Quantité brut + Unité (budgets individuels uniquement) -->
          <template v-if="!isGeneration">
            <div class="column is-3">
              <b-field
                custom-class="mb-4px"
                :type="error.quantiteBrut ? 'is-danger' : ''">
                <template slot="label">
                  <qd-label
                    :label="$t('creerPanneau.labelChamp.quantiteBrut')"
                    :required="true" />
                </template>
                <b-input
                  type="number"
                  v-model.number="budget.quantiteBrut"
                  @input="onQuantiteBrutInput"
                  :disabled="disabled"
                  :use-html5-validation="false"/>
              </b-field>
            </div>
            <div class="column is-3">
              <b-field
                custom-class="mb-4px"
                :type="error.uniteQteBrut ? 'is-danger' : ''">
                <template slot="label">
                  <qd-label
                    :label="$t('creerPanneau.labelChamp.uniteQteBrut')"
                    :required="true" />
                </template>
                <b-select
                  v-model="budget.uniteQteBrut"
                  expanded
                  :disabled="disabled">
                  <option
                    v-for="unite in typesQuantite"
                    :key="unite.code"
                    :value="unite.code">
                    {{ $t(unite.libelle) }}
                  </option>
                </b-select>
              </b-field>
            </div>
            <div class="column is-3">
              <b-field
                custom-class="mb-4px"
                :type="error.prixUnitaireBrut ? 'is-danger' : ''">
                <template slot="label">
                  <qd-label
                    :label="$t('creerPanneau.labelChamp.prixUnitaireBrut')"
                    :required="true" />
                </template>
                <input-currency
                  v-model="budget.prixUnitaireBrut"
                  @input="onPrixUnitaireBrutInput"
                  :disabled="disabled"/>
              </b-field>
            </div>

            <div class="column is-3">
              <b-field
                custom-class="mb-4px">
                <template slot="label">
                  <qd-label :label="$t('creerPanneau.labelChamp.remise')"/>
                </template>
                <b-input
                  type="number"
                  v-model="budget.remise.remise"
                  @input="() => reloadCalcul(false)"
                  :disabled="disabled"
                  :use-html5-validation="false"/>
              </b-field>
            </div>
          </template>

          <!-- Budget brut HT -->
          <div class="column is-5" :class="{ 'is-5': isGeneration, 'is-6': !isGeneration }">
            <b-field
              custom-class="mb-4px"
              :type="error.prixBrutNegocie ? 'is-danger' : ''">
              <template slot="label">
                <qd-label
                  :label="isGeneration
                    ? $t('creerPanneau.labelChamp.prixBrutNegocieAnnuel')
                    : $t('creerPanneau.labelChamp.prixBrutNegocie')"
                  :required="true" />
              </template>
              <input-currency
                v-model="budget.prixBrutNegocie"
                @input="onPrixBrutNegocieInput"
                :disabled="disabled"/>
            </b-field>
          </div>

          <div v-if="isGeneration" class="column is-2">
            <b-field
              custom-class="mb-4px">
              <template slot="label">
                <qd-label :label="$t('creerPanneau.labelChamp.remise')"/>
              </template>
              <b-input
                type="number"
                v-model="budget.remise.remise"
                @input="() => reloadCalcul(false)"
                :disabled="disabled"
                :use-html5-validation="false"/>
            </b-field>
          </div>

          <!-- Budget net HT -->
          <div class="column is-5" :class="{ 'is-5': isGeneration, 'is-6': !isGeneration }">
            <b-field
              custom-class="mb-4px"
              :type="error.prixNet ? 'is-danger' : ''">
              <template slot="label">
                <qd-label
                  :label="isGeneration
                    ? $t('creerPanneau.labelChamp.prixNetAnnuel')
                    : $t('creerPanneau.labelChamp.prixNet')"
                  :required="true" />
              </template>
              <input-currency
                v-model="budget.prixNet"
                @input="() => reloadCalcul(true)"
                :disabled="disabled"/>
            </b-field>
          </div>

          <!-- Prix unitaire TLPE + Quantité TLPE + Unité (budgets individuels uniquement) -->
          <template v-if="!isGeneration">
            <div class="column is-3">
              <b-field
                custom-class="mb-4px"
                :type="error.quantiteTlpe ? 'is-danger' : ''">
                <template slot="label">
                  <qd-label
                    :label="$t('creerPanneau.labelChamp.quantiteTlpe')"
                    :required="true" />
                </template>
                <b-input
                  type="number"
                  v-model.number="budget.quantiteTlpe"
                  @input="onQuantiteTlpeInput"
                  :disabled="disabled"
                  :use-html5-validation="false"/>
              </b-field>
            </div>
            <div class="column is-3">
              <b-field
                custom-class="mb-4px"
                :type="error.uniteQteTlpe ? 'is-danger' : ''">
                <template slot="label">
                  <qd-label
                    :label="$t('creerPanneau.labelChamp.uniteQteTlpe')"
                    :required="true" />
                </template>
                <b-select
                  v-model="budget.uniteQteTlpe"
                  expanded
                  :disabled="disabled">
                  <option
                    v-for="unite in typesQuantite"
                    :key="unite.code"
                    :value="unite.code">
                    {{ $t(unite.libelle) }}
                  </option>
                </b-select>
              </b-field>
            </div>
            <div class="column is-3">
              <b-field
                custom-class="mb-4px"
                :type="error.prixUnitaireTlpe ? 'is-danger' : ''">
                <template slot="label">
                  <qd-label
                    :label="$t('creerPanneau.labelChamp.prixUnitaireTlpe')"
                    :required="true" />
                </template>
                <input-currency
                  v-model="budget.prixUnitaireTlpe"
                  @input="onPrixUnitaireTlpeInput"
                  :disabled="disabled"/>
              </b-field>
            </div>
          </template>

          <!-- TLPE -->
          <div class="column" :class="{ 'is-6': isGeneration, 'is-3': !isGeneration }">
            <b-field custom-class="mb-4px">
              <template slot="label">
                <qd-label :label="$t('creerPanneau.labelChamp.tlpe')" />
              </template>
              <input-currency
                v-model="budget.tlpe"
                @input="onTlpeInput"
                :disabled="disabled"/>
            </b-field>
          </div>

          <!-- Frais techniques -->
          <div class="column is-6">
            <b-field custom-class="mb-4px">
              <template slot="label">
                <qd-label :label="$t('creerPanneau.labelChamp.fraisTechnique')" />
              </template>
              <input-currency
                v-model="budget.fraisTechnique"
                :disabled="disabled"/>
            </b-field>
          </div>

          <div v-if="!isGeneration" class="column is-6"></div>

          <!-- Honoraires -->
          <div class="column is-6">
            <b-field custom-class="mb-4px">
              <template slot="label">
                <qd-label :label="$t('creerPanneau.labelChamp.honoraires')" />
              </template>
              <input-currency v-model="budget.honoraires" disabled :bg3="true"/>
            </b-field>
          </div>
          <div class="column is-6">
            <b-field custom-class="mb-4px">
              <template slot="label">
                <qd-label :label="$t('creerPanneau.labelChamp.honoraireFraisTraitement')" />
              </template>
              <input-currency
                v-model="budget.honoraireFraisTraitement" disabled :bg3="true"/>
            </b-field>
          </div>

          <!-- Calculer honoraires -->
          <div class="column is-12 pltb-10 mt-20">
            <div class="is-flex">
              <b-button
                type="is-primary"
                class="mr-10"
                @click="modalIsActive = !modalIsActive"
                :disabled="disabled">
                <p class="is-size-14px has-text-weight-bold">
                  {{$t('creerPanneau.btnCalculerHonoraire')}}
                </p>
              </b-button>
              <b-button
                v-if="isGeneration"
                type="is-success"
                :disabled="disabled"
                @click="$emit('genererBudgets')">
                <p class="is-size-14px has-text-weight-bold">
                  {{ $t('creerPanneau.btnGenerer') }}
                </p>
              </b-button>
              <b-button
                v-if="!isGeneration"
                type="is-danger"
                :disabled="disabled"
                @click="$emit('supprimer')">
                <b-icon icon="delete"/>
              </b-button>
            </div>
          </div>
        </div>
      </div>
    </b-collapse>
    <ModalCalculHonoraire v-if="modalIsActive"
      :modal-is-active="modalIsActive"
      :modeCalculHonoraire="budget.modeCalculHonoraire"
      @modalIsClose="modalIsActive = $event"
      @setHonoraires="updateHonoraire($event)"/>
  </div>
</template>

<script>
import assign from 'lodash/assign';
import { createNamespacedHelpers } from 'vuex';
import QdLabel from '@/components/general/qdLabel.vue';
import MsgError from '@/components/general/messageErreur';
import InputCurrency from '@/components/general/inputCurrency.vue';
import ModalCalculHonoraire from '@/components/general/modalCalculHonoraire.vue';
import { formatNombre } from '@/services/general/general';
import { calcPrixTotal } from '@/services/gestionPanneau/calcPanneau';
import { calculerDateFinPremiereEcheance, calculerDateFinBudgetaire } from '@/services/ligneCommande/calculDataLigne';
import updateModeCalculHonoraire from '@/services/gestionPanneau/updateModeCalculHonoraire';
import { genererRemiseFromTauxNegocie } from '@/services/devis/gestionTauxNegocies';
import { calculNetBudget, calculRemiseBudget } from '@/services/devisCommandes/calcul/calculRemisePanneau';
import { updateModeCalculHonoraireBudget } from  '@/services/devisCommandes/calcul/calculHonoraire';
import { getTarif } from '@/services/gestionPanneau/getGrilleTarif';
import { getMsgErrorBudget, genererObjectErrorBudget } from '@/services/gestionPanneau/validationPanneau';
import calculArrondi from '@/services/calcul/calculArrondi';
import { TYPES_QUANTITE } from '@/configuration/devisConf';

const Recommandations = createNamespacedHelpers('recommandations');
export default {
  name: 'AppBudget',
  props: {
    value: {
      type: Boolean,
      default: true,
    },
    budget: {
      type: Object,
      required: true,
    },
    title: {
      type: String,
      default: '',
    },
    isGeneration: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Object,
      default: () => ({}),
    },
    panneau: {
      type: Object,
      default: null,
    },
    regies: {
      type: Array,
      default: () => [],
    },
  },
  components: {
    QdLabel,
    MsgError,
    InputCurrency,
    ModalCalculHonoraire,
  },
  data() {
    return {
      modalIsActive: false,
      func: { formatNombre },
      typesQuantite: TYPES_QUANTITE,
    };
  },
  watch: {
    budget: {
      handler(value){
        this.recalculerHonoraires();
        this.budget.prixTotal = calcPrixTotal(value);
      },
      deep: true,
      immediate: true,
    },
  },
  computed: {
    ...Recommandations.mapGetters({
      honoraireForfaitaire: 'honoraireForfaitaire',
      pointDeVente: 'pointDeVente',
    }),
    isOpen: {
      get() { return this.value; },
      set(val) { this.$emit('input', val); },
    },
    hasError() {
      return this.error && Object.values(this.error).some(Boolean);
    },
    disabled() {
      return this.$route.name === 'view-panneau';
    },
    msgErrors() {
      return getMsgErrorBudget(this.error).map(key => this.$t(key));
    },
  },
  methods: {
    ...Recommandations.mapActions({
      setLigneHonoraireForfaitaire: 'setLigneHonoraireForfaitaire',
    }),
    msgErrorClose() {
      this.$nextTick(() => {
        assign(this.error, genererObjectErrorBudget());
      })
    },
    async onDateDebutInput(dateDebut) {
      if (this.isGeneration) {
        this.budget.dateFin = calculerDateFinBudgetaire(dateDebut, this.budget.dateFin, this.pointDeVente);
        this.budget.dateFinPremiereEcheance = calculerDateFinPremiereEcheance(dateDebut, this.budget.dateFin, this.pointDeVente);
        await this.chargerEtAppliquerTauxNegocie(dateDebut);
      }
      if (dateDebut) updateModeCalculHonoraire(this.budget);
      this.initialiseFraisTech();
    },
    async onDateFinInput(dateFin) {
      if (!this.isGeneration) return;
      this.budget.dateFinPremiereEcheance = calculerDateFinPremiereEcheance(this.budget.dateDebut, dateFin, this.pointDeVente);
    },
    async chargerEtAppliquerTauxNegocie(dateDebut) {
      if (!this.panneau || !this.panneau.regieId || !dateDebut) return;
      const anneeDate = new Date(dateDebut).getFullYear();
      const regie = this.regies.find(r => r.id === this.panneau.regieId);
      if (!regie || !regie.tauxNegociations) return;
      const taux = regie.tauxNegociations.find(tn => {
        return tn.annee === anneeDate && tn.actif;
      });
      if (!taux) return;
      this.budget.remise = await genererRemiseFromTauxNegocie(taux);
      calculNetBudget(this.budget);
    },
    reloadCalcul(isPrixNet = false) {
      if (isPrixNet) {
        calculRemiseBudget(this.budget);
      } else {
        calculNetBudget(this.budget);
      }
    },
    onPrixBrutNegocieInput(prixBrutNegocie) {
      if (!this.isGeneration && this.budget.prixUnitaireBrut > 0) {
        this.budget.quantiteBrut = calculArrondi(prixBrutNegocie / this.budget.prixUnitaireBrut, 5);
      }
      this.reloadCalcul(false);
    },
    onTlpeInput(tlpe) {
      if (!this.isGeneration && this.budget.prixUnitaireTlpe > 0) {
        this.budget.quantiteTlpe = calculArrondi(tlpe / this.budget.prixUnitaireTlpe, 5);
      }
    },
    onPrixUnitaireBrutInput(prixUnitaireBrut) {
      const quantite = this.budget.quantiteBrut != null ? this.budget.quantiteBrut : 1;
      this.budget.prixBrutNegocie = calculArrondi(prixUnitaireBrut * quantite, 5);
      this.reloadCalcul(false);
    },
    onQuantiteBrutInput(quantite) {
      const prixUnitaire = this.budget.prixUnitaireBrut != null ? this.budget.prixUnitaireBrut : 0;
      this.budget.prixBrutNegocie = calculArrondi(prixUnitaire * quantite, 5);
      this.reloadCalcul(false);
    },
    onPrixUnitaireTlpeInput(prixUnitaireTlpe) {
      const quantite = this.budget.quantiteTlpe != null ? this.budget.quantiteTlpe : 1;
      this.budget.tlpe = calculArrondi(prixUnitaireTlpe * quantite, 5);
    },
    onQuantiteTlpeInput(quantite) {
      const prixUnitaire = this.budget.prixUnitaireTlpe != null ? this.budget.prixUnitaireTlpe : 0;
      this.budget.tlpe = calculArrondi(prixUnitaire * quantite, 5);
    },
    initialiseFraisTech() {
      if (!this.pointDeVente || !this.budget.dateDebut) return;
      getTarif({
        enseigneId: this.pointDeVente.tiersParentId,
        dateVisu: this.budget.dateDebut,
        formatId: this.panneau ? this.panneau.formatId : null,
        produitFaceId: this.panneau ? this.panneau.id : null,
      }, this.budget);
    },
    updateHonoraire(modeCalcul) {
      updateModeCalculHonoraireBudget(this.budget, modeCalcul);
      if (!this.honoraireForfaitaire) {
        this.setLigneHonoraireForfaitaire(modeCalcul.montantHonoraireForfaitaire);
      }
    },
    recalculerHonoraires() {
      if (!this.budget.modeCalculHonoraire) return;
      updateModeCalculHonoraireBudget(this.budget, this.budget.modeCalculHonoraire);
    },
  },
  mounted() {

    if (this.budget.dateDebut && this.panneau.ancienPanneau === "NON") {
      updateModeCalculHonoraire(this.budget);
    }
  },
};
</script>
